import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { ArrowRight, Check } from "@phosphor-icons/react";

const PROJECT_TYPES = ["Residential", "Commercial", "Hospitality", "Other"];
const BUDGETS = ["Under $50k", "$50k – $150k", "$150k – $500k", "$500k +", "Prefer not to say"];

const EMPTY = { name: "", email: "", phone: "", project_type: "", budget: "", message: "", company: "" };

export default function ContactForm() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-16" data-testid="contact-success">
        <div className="mx-auto h-16 w-16 rounded-full border border-accent/40 grid place-items-center text-accent">
          <Check size={28} />
        </div>
        <h3 className="mt-8 font-serif font-light text-4xl md:text-5xl">Thank you — we'll be in touch shortly.</h3>
        <p className="mt-4 text-foreground/60">Your enquiry has reached the studio. We'll respond personally soon.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="w-full" data-testid="contact-form">
      {/* honeypot: hidden from humans, bots tend to fill it */}
      <input
        type="text"
        name="company"
        tabIndex={-1}
        autoComplete="off"
        value={form.company}
        onChange={(e) => set("company", e.target.value)}
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
        <Field label="Name" required>
          <input data-testid="contact-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Email" required>
          <input data-testid="contact-email" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Phone">
          <input data-testid="contact-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Project Type">
          <select data-testid="contact-project-type" value={form.project_type} onChange={(e) => set("project_type", e.target.value)} className={inputCls}>
            <option value="">Select…</option>
            {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Budget Range">
            <select data-testid="contact-budget" value={form.budget} onChange={(e) => set("budget", e.target.value)} className={inputCls}>
              <option value="">Select…</option>
              {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Tell us about your space">
            <textarea data-testid="contact-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls} />
          </Field>
        </div>
      </div>

      <div className="mt-12 flex justify-center">
        <button
          type="submit"
          disabled={busy}
          data-testid="contact-submit"
          className="inline-flex items-center gap-6 rounded-full bg-foreground text-background px-12 py-5 text-sm uppercase tracking-[0.2em] hover:bg-accent transition-colors duration-500 disabled:opacity-50"
        >
          {busy ? "Sending…" : "Send Enquiry"}
          <ArrowRight size={18} />
        </button>
      </div>
    </form>
  );
}

const inputCls = "w-full bg-transparent border-b border-foreground/25 py-3 text-foreground focus:outline-none focus:border-foreground transition-colors";
function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">
        {label}{required && <span className="text-accent"> *</span>}
      </label>
      {children}
    </div>
  );
}
